/**
 * Display helpers for the air risk class (ARC) by JARUS SORA 2.5 Figure 6 over the ŘLP UAS zones.
 *
 * The backend explains the Figure 6 ARC only in English text lines (arc_details.factors of the SORA
 * pre-assessment, reasons/recommendations of the airspace advisory, airspace_factors of CheckMate),
 * except the reasons of undetermined airspace, which also come as Czech/English ProvisionalReasons.
 * These helpers read the fixed line formats of SoraFigure6ArcEngine, SoraAirRiskInputBuilder,
 * AirspaceService and SoraOptimizationEngine; a line in an unknown format is shown as sent. Texts come
 * from the `arcDisplay` section of the locales (`t` is the whole locale object).
 */
import { localizeReasonMessage } from './provisionalReasons';

const FIGURE6_METHOD_PREFIX = 'Method: JARUS SORA 2.5 Figure 6';

/** Fills `{name}` placeholders of a locale template. */
export const fillTemplate = (template, params = {}) =>
  Object.entries(params).reduce(
    (text, [key, value]) => text.split(`{${key}}`).join(String(value)),
    template || '',
  );

/**
 * What the Figure 6 factors of arc_details say: whether the ARC comes from Figure 6 at all (legacy
 * airport-proximity factors are not), the airspace encounter category (AEC) of the airspace itself
 * (`aecWithoutDeclaration`, also when an ARC-a declaration replaced it), the CTR/MCTR and 1000 ft
 * nodes, and the ATS/ATC/TMPR notes.
 */
export const parseArcFactors = (factors) => {
  const result = {
    figure6: false,
    aec: null,
    aecWithoutDeclaration: null,
    declarationApplied: false,
    inCtr: false,
    above1000Ft: false,
    atsCoordination: false,
    atcAgreement: false,
    tmpr: null,
  };
  for (const factor of factors || []) {
    if (typeof factor !== 'string') continue;
    if (factor.startsWith(FIGURE6_METHOD_PREFIX)) result.figure6 = true;
    let match = /^Without the declaration: AEC (\d+):/.exec(factor);
    if (match) {
      result.aecWithoutDeclaration = Number(match[1]);
      result.declarationApplied = true;
      continue;
    }
    match = /^AEC (\d+):/.exec(factor);
    if (match) result.aec = Number(match[1]);
    if (factor.startsWith('Controlled airspace: yes (CTR/MCTR')) result.inCtr = true;
    if (factor.startsWith('Controlled airspace: yes (above 1000 ft AGL')) result.above1000Ft = true;
    if (factor.startsWith('Inside CTR/MCTR: coordination with ATS')) result.atsCoordination = true;
    if (factor.startsWith('In ARC-d airspace an agreement with ATC')) result.atcAgreement = true;
    match = /^TMPR \(Table 6, BVLOS\): (\w+)$/.exec(factor);
    if (match) result.tmpr = match[1].toLowerCase();
  }
  if (result.aecWithoutDeclaration === null) result.aecWithoutDeclaration = result.aec;
  return result;
};

/**
 * The SAIL banner's airspace reason for the Figure 6 ARC: the undetermined airspace, or the concrete
 * cause of a high-risk airspace (airport environment in a CTR/MCTR, above 500 ft AGL in a CTR/MCTR,
 * above 1000 ft AGL). Null for a legacy (airport-proximity) ARC and for any other status — the
 * banner then keeps its previous behaviour.
 */
export const arcBannerReason = (arcDetails, t) => {
  const texts = t?.arcDisplay || {};
  const parsed = parseArcFactors(arcDetails?.factors);
  if (!parsed.figure6) return null;
  const status = arcDetails?.airspace_status;
  if (status === 'unknown') return texts.airspaceUndetermined;
  if (status !== 'high_risk') return null;

  let reason;
  if (parsed.aecWithoutDeclaration === 1) {
    reason = texts.highRiskAirportCtr;
  } else if (parsed.aecWithoutDeclaration === 3 && parsed.inCtr) {
    reason = texts.highRiskAbove500Ctr;
  } else if (parsed.aecWithoutDeclaration === 3) {
    reason = texts.highRiskAbove1000;
  } else {
    reason = t?.sailBanner?.elevatedAirspaceRisk;
  }
  return parsed.declarationApplied ? fillTemplate(texts.withoutDeclaration, { reason }) : reason;
};

/** Localized TMPR note of a residual-ARC level (`high`, `medium`, `low`, `none`). */
const tmprNote = (level, t) => {
  const texts = t?.arcDisplay || {};
  return fillTemplate(texts.tmpr, { level: texts.tmprLevels?.[level] || level });
};

/**
 * Notes of the Figure 6 ARC for the mission detail: coordination with ATS inside a CTR/MCTR, an ATC
 * agreement in ARC-d airspace (VLOS) and the TMPR of a BVLOS operation, as the factors carry them.
 */
export const arcNotes = (arcDetails, t) => {
  const parsed = parseArcFactors(arcDetails?.factors);
  if (!parsed.figure6) return [];
  const texts = t?.arcDisplay || {};
  const notes = [];
  if (parsed.atsCoordination) notes.push(texts.atsCoordination);
  if (parsed.atcAgreement) notes.push(texts.atcAgreement);
  if (parsed.tmpr) notes.push(tmprNote(parsed.tmpr, t));
  return notes;
};

/** Residual ARC → TMPR level (SORA 2.5 Main Body Table 6). */
const TMPR_BY_ARC = { 'ARC-d': 'high', 'ARC-c': 'medium', 'ARC-b': 'low', 'ARC-a': 'none' };

const ZONE_LINE = /^Zone (.+?) \(([^,()]+), (CTR\/MCTR class D|airport\/heliport environment)\): (.*)$/;

/** Whether CheckMate's airspace_factors come from Figure 6 (the operational-volume line is always there). */
const isFigure6AirspaceFactors = (factors) =>
  (factors || []).some(f => typeof f === 'string' && f.startsWith('Operational volume:'));

/**
 * Whether CheckMate's airspace is undetermined by Figure 6 — the worst-case ARC that blocks the fast
 * track and the approval. A legacy (airport-proximity) assessment also reports `unknown` for a mission
 * without take-off coordinates, but neither assumes the worst ARC nor rejects the fast track.
 */
export const isFigure6UndeterminedAirspace = (state) =>
  state?.airspace_status === 'unknown'
  && (isFigure6AirspaceFactors(state?.environment_auto_detected?.airspace_factors)
    || (state?.provisional_reasons || []).some(r => provisionalReasonGroup(r.code) === 'airspace'));

/**
 * The same notes for CheckMate, which has no arc_details: inside a CTR/MCTR when a listed zone is a
 * class D CTR/MCTR over determined airspace, and the TMPR of BVLOS from the final ARC.
 */
export const checkMateArcNotes = (state, t) => {
  const factors = state?.environment_auto_detected?.airspace_factors;
  if (!isFigure6AirspaceFactors(factors)) return [];
  const texts = t?.arcDisplay || {};
  const notes = [];
  const determined = state?.airspace_status !== 'unknown';
  if (determined && factors.some(f => ZONE_LINE.exec(f)?.[3] === 'CTR/MCTR class D')) {
    notes.push(texts.atsCoordination);
  }
  const tmpr = TMPR_BY_ARC[state?.current_arc];
  if ((state?.operation_type || '').toUpperCase() === 'BVLOS' && tmpr) {
    notes.push(tmprNote(tmpr, t));
  }
  return notes;
};

// ── Provisional reasons ──────────────────────────────────────────────────────────────────────────

/** Group of a ProvisionalReason code: the undetermined airspace (`airspace_*`, `arc_*`), the ARC-a
 * declaration (`atypical_declaration_*`) or the simplified GRC calculation (everything else). */
export const provisionalReasonGroup = (code) => {
  if (typeof code === 'string' && (code.startsWith('airspace_') || code.startsWith('arc_'))) return 'airspace';
  if (typeof code === 'string' && code.startsWith('atypical_declaration_')) return 'atypical';
  return 'grc';
};

const GROUP_ORDER = ['airspace', 'atypical', 'grc'];

/** The reasons split into the non-empty groups, the undetermined airspace first. */
export const groupProvisionalReasons = (reasons) =>
  GROUP_ORDER
    .map(group => ({ group, reasons: (reasons || []).filter(r => provisionalReasonGroup(r.code) === group) }))
    .filter(entry => entry.reasons.length > 0);

/** Whether any reason is about the ARC (undetermined airspace or the ARC-a declaration). */
export const hasArcProvisionalReason = (reasons) =>
  (reasons || []).some(r => provisionalReasonGroup(r.code) !== 'grc');

/** Heading of a reason group: the undetermined airspace, the accepted or ignored ARC-a declaration,
 * or the simplified calculation. */
export const provisionalGroupTitle = (group, reasons, t) => {
  const texts = t?.arcDisplay || {};
  if (group === 'airspace') return texts.provisionalAirspaceTitle;
  if (group === 'atypical') {
    return (reasons || []).some(r => r.code === 'atypical_declaration_accepted')
      ? texts.provisionalAtypicalAcceptedTitle
      : texts.provisionalAtypicalIgnoredTitle;
  }
  return texts.provisionalSimplifiedTitle;
};

/** Whether a reason group warrants the warning colour although it has nothing to complete: the
 * undetermined airspace, and an ARC-a declaration that was ignored or not evaluated. */
export const isWarningGroup = (group, reasons) =>
  group === 'airspace'
  || (group === 'atypical' && !(reasons || []).every(r => r.code === 'atypical_declaration_accepted'));

// ── Explanation lines ────────────────────────────────────────────────────────────────────────────

/**
 * One part of a "cs / en" line (CheckMate's undetermined-airspace reasons carry both languages in one
 * text): the part of `language`; the text itself when it has no single " / " separator.
 */
export const splitBilingual = (text, language) => {
  if (typeof text !== 'string') return text;
  const parts = text.split(' / ');
  if (parts.length !== 2) return text;
  return language === 'cs' ? parts[0] : parts[1];
};

const RECOMMENDATIONS = {
  'Resolve the reasons above: until the air risk class is determined the mission can be neither approved nor fast-tracked': 'recResolve',
  'Inside CTR/MCTR: coordination with ATS is likely required (telephone or continuous radio contact)': 'atsCoordination',
  'Verify authorization requirements before proceeding': 'recVerifyAuthorization',
  'Review local restrictions and consider risk mitigations': 'recReviewRestrictions',
  'Standard pre-flight checks and procedures apply': 'recStandardChecks',
};

/**
 * A localized line explaining the Figure 6 ARC — an item of the airspace advisory's reasons or
 * recommendations, or of CheckMate's airspace_factors. A reason of undetermined airspace is matched
 * against `provisionalReasons` (its English text, or "cs / en" in CheckMate) and shown in the
 * language; known line formats are rebuilt from the locale; anything else is shown as sent.
 */
export const localizeArcLine = (line, language, t, provisionalReasons = []) => {
  if (typeof line !== 'string') return line;
  const texts = t?.arcDisplay || {};

  const reason = (provisionalReasons || []).find(r =>
    line === r.message_en
    || line === r.message
    || line === `${r.message} / ${r.message_en || r.message}`);
  if (reason) return localizeReasonMessage(reason, language);

  const recommendation = RECOMMENDATIONS[line];
  if (recommendation) return texts[recommendation] || line;

  let match = /^AEC (\d+): .*? → initial (ARC-[a-d])(?:, final (ARC-[a-d]))?$/.exec(line);
  if (match && texts.aec?.[match[1]]) {
    const params = { aec: match[1], description: texts.aec[match[1]], initial: match[2], final: match[3] };
    return fillTemplate(match[3] ? texts.lineAecFinal : texts.lineAec, params);
  }
  match = /^Worst case — the airspace could not be determined → (ARC-[a-d])$/.exec(line);
  if (match) return fillTemplate(texts.lineWorstCase, { arc: match[1] });
  match = /^Operational volume: flight geography \+ (\S+) m contingency \(1:1\), top (\S+) m AGL( \(maximum altitude missing — worst case\))?$/.exec(line);
  if (match) {
    return fillTemplate(texts.lineVolume, { contingency: match[1], top: match[2] })
      + (match[3] ? texts.lineVolumeAltitudeMissing : '');
  }
  match = ZONE_LINE.exec(line);
  if (match) {
    const role = match[3] === 'CTR/MCTR class D' ? texts.zoneRoleCtr : texts.zoneRoleAirport;
    return fillTemplate(texts.lineZone, { ident: match[1], layer: match[2], role, limit: match[4] });
  }
  match = /^… and (\d+) more zones$/.exec(line);
  if (match) return fillTemplate(texts.lineMoreZones, { count: match[1] });
  match = /^ŘLP zone data for (\S+): release (.*); boundary (.*); data (.*)$/.exec(line);
  if (match) {
    const boundary = match[3] === 'none' ? (texts.boundaryNone || match[3]) : match[3];
    return fillTemplate(texts.lineData, { date: match[1], release: match[2], boundary, fingerprint: match[4] });
  }
  match = /^No zone data effective on the mission day; the earliest data, effective later, were used \((.*)\)$/.exec(line);
  if (match) return fillTemplate(texts.lineDataLater, { layers: match[1] });

  return splitBilingual(line, language);
};
